import { ReactNode } from "react";

export type LegalDoc = {
  key: "terms" | "privacy" | "accessibility";
  title: string;
  updated: string;
  content: ReactNode;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="mb-6">
    <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
    <div className="text-foreground/80 leading-relaxed space-y-2">{children}</div>
  </section>
);

const List = ({ items }: { items: ReactNode[] }) => (
  <ul className="list-disc pr-5 space-y-1">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export const LEGAL_DOCS: Record<LegalDoc["key"], LegalDoc> = {
  terms: {
    key: "terms",
    title: "תקנון ותנאי שימוש",
    updated: "אפריל 2026",
    content: (
      <>
        <p className="mb-4 text-foreground/80 leading-relaxed">
          ברוכים הבאים לאתר orbagag.com (להלן: "האתר"), המופעל על ידי אור פריאס (להלן: "הנהלת האתר" או "העסק").
          השימוש באתר, בתכנים ובשירותים המוצעים בו כפוף לאמור בתקנון זה. גלישה באתר או פנייה דרך האתר מהווה הסכמה
          מצדך לתנאים המפורטים להלן. אם אינך מסכים לתנאי התקנון, הנך מתבקש לחדול משימוש באתר.
        </p>
        <Section title="1. כללי">
          <List
            items={[
              "התקנון מנוסח בלשון זכר מטעמי נוחות בלבד, אך הוא פונה לכל המגדרים.",
              "האתר משמש כאתר תדמית המציע שירותי גגות, פחחות, איטום ובנייה קלה וכן יצירת קשר עם העסק.",
              "הנהלת האתר שומרת לעצמה את הזכות לעדכן את תנאי השימוש מעת לעת, לפי שיקול דעתה הבלעדי וללא הודעה מוקדמת.",
            ]}
          />
        </Section>
        <Section title="2. קניין רוחני">
          <List
            items={[
              "כל זכויות הקניין הרוחני באתר, לרבות עיצובו, קוד המקור, טקסטים, תמונות, סרטונים, לוגואים וסימני מסחר שייכים באופן בלעדי לאור פריאס או לצדדים שלישיים שהעניקו רשות שימוש בהם.",
              "אין להעתיק, לשכפל, להפיץ או למכור תוכן מהאתר ללא אישור מפורש בכתב מהנהלת האתר.",
            ]}
          />
        </Section>
        <Section title="3. הגבלת אחריות">
          <List
            items={[
              "התכנים המופיעים באתר מוצגים כפי שהם (As-Is). למרות שאנו שואפים לדיוק מרבי, ייתכנו טעויות, השמטות או אי-דיוקים. המידע באתר אינו מהווה ייעוץ מקצועי מחייב.",
              "הנהלת האתר לא תישא באחריות לכל נזק (ישיר או עקיף) שייגרם למשתמש או לצד שלישי כתוצאה מהסתמכות על התוכן באתר או כתוצאה משימוש בו.",
              "הנהלת האתר אינה אחראית לתקלות טכניות הנובעות מתשתית האינטרנט, מאחסון האתר או ממכשירו של המשתמש.",
            ]}
          />
        </Section>
        <Section title="4. קישורים לאתרים חיצוניים">
          <p>
            האתר עשוי להכיל קישורים (Links) לאתרים חיצוניים. הנהלת האתר אינה אחראית לתוכן אתרים אלו, למדיניות
            הפרטיות שלהם או לכל תוצאה שתיווצר מהשימוש בהם.
          </p>
        </Section>
        <Section title="5. שימוש אסור באתר">
          <p>המשתמש מתחייב שלא לבצע את הפעולות הבאות:</p>
          <List
            items={[
              "שימוש באתר לכל מטרה בלתי חוקית.",
              "ביצוע פעולות העלולות לפגוע בתקינות האתר, לרבות החדרת וירוסים, תוכנות זדוניות וביצוע התקפות סייבר.",
              "איסוף נתונים מהאתר באמצעות רובוטים, סקרייפינג (Scraping) או כל כלי אוטומטי אחר ללא אישור בכתב.",
            ]}
          />
        </Section>
        <Section title="6. שיפוי">
          <p>
            המשתמש מתחייב לשפות את הנהלת האתר בגין כל נזק, הפסד או הוצאה (לרבות שכר טרחת עו"ד) שייגרמו לה עקב הפרת
            תנאי שימוש אלו או פגיעה בזכויות צד שלישי.
          </p>
        </Section>
        <Section title="7. סמכות שיפוט">
          <p>
            על תקנון זה יחולו אך ורק דיני מדינת ישראל. מקום השיפוט הבלעדי לכל עניין הנוגע להסכם זה יהיה בבתי המשפט
            המוסמכים במחוז מרכז.
          </p>
        </Section>
        <Section title="8. יצירת קשר">
          <p>לכל שאלה, הבהרה או דיווח על תקלה, ניתן ליצור עמנו קשר:</p>
          <List
            items={[
              "שם: אור פריאס",
              <span key="t">טלפון: <span dir="ltr">054-572-5681</span></span>,
              "דוא\"ל: OrBagag@gmail.com",
            ]}
          />
        </Section>
      </>
    ),
  },
  privacy: {
    key: "privacy",
    title: "מדיניות פרטיות",
    updated: "אפריל 2026",
    content: (
      <>
        <p className="mb-4 text-foreground/80 leading-relaxed">
          מפעיל האתר, אור פריאס (להלן: "העסק" או "אנחנו"), מכבד את פרטיותם של המשתמשים באתר orbagag.com (להלן:
          "האתר"). מסמך זה מפרט את האופן בו אנו אוספים מידע, משתמשים בו ושומרים עליו. הגלישה והשימוש באתר מהווים
          הסכמה למדיניות זו.
        </p>
        <Section title="1. המידע הנאסף באתר">
          <p>אנו עשויים לאסוף שני סוגי מידע:</p>
          <List
            items={[
              <><strong>מידע אישי שנמסר מרצון:</strong> בעת מילוי טופס יצירת קשר באתר, תתבקש למסור פרטים כגון שם מלא, מספר טלפון וכתובת דואר אלקטרוני.</>,
              <><strong>מידע טכני (סטטיסטי):</strong> מידע על אופן הגלישה, כתובת IP, סוג הדפדפן, זמן שהייה באתר והדפים בהם ביקרת. מידע זה נאסף באופן אנונימי ואינו מזהה אותך אישית.</>,
            ]}
          />
        </Section>
        <Section title="2. המטרה לשמה אנו אוספים מידע">
          <List
            items={[
              "יצירת קשר עם הלקוח למתן שירות או הצעת מחיר.",
              "שיפור חוויית המשתמש וניהול תקין של האתר.",
              "ניתוח סטטיסטי של תנועת הגולשים באתר.",
              "לצרכי שיווק ופרסום (במידה והמשתמש נתן את הסכמתו המפורשת לכך).",
            ]}
          />
        </Section>
        <Section title="3. העברת מידע לצדדים שלישיים">
          <p>אנו לא נעביר את פרטיך האישיים לצדדים שלישיים, למעט במקרים הבאים:</p>
          <List
            items={[
              "במידה ונדרש לעשות זאת על פי צו שיפוטי או הוראת חוק.",
              "לצורך תפעול האתר (לדוגמה: שירותי אחסון או מערכות לניהול קשרי לקוחות), תחת התחייבותם לשמירה על סודיות.",
              "במקרה של הליכים משפטיים בין המשתמש לבין העסק.",
            ]}
          />
        </Section>
        <Section title="4. שימוש ב-Cookies (עוגיות)">
          <p>
            האתר עושה שימוש ב"עוגיות" (Cookies) לצורך תפעולו השוטף והתקין, ובכלל זה כדי לאסוף נתונים סטטיסטיים על
            השימוש באתר ולאימות פרטים. באפשרותך לשנות את הגדרות הדפדפן שלך כך שיסרב לקבל עוגיות, אולם הדבר עלול לפגוע
            בחלק מהשירותים הניתנים באתר.
          </p>
        </Section>
        <Section title="5. שירותי צד שלישי (Analytics ופרסום)">
          <p>
            האתר עשוי להשתמש בכלי ניתוח של צדדים שלישיים, כגון Google Analytics ופיקסלים של רשתות חברתיות, המסייעים
            לנו להבין כיצד גולשים באתר ולבצע התאמות של מודעות פרסום בהתאם לתחומי העניין של המשתמש.
          </p>
        </Section>
        <Section title="6. אבטחת מידע">
          <p>
            אנו מיישמים באתר מערכות ונהלים עדכניים לאבטחת מידע כדי לצמצם את הסיכון לחדירה בלתי-מורשית, אך אין ביכולתנו
            להבטיח חסינות מוחלטת מפני פריצות או גישה לא מורשית למאגרי המידע.
          </p>
        </Section>
        <Section title="7. זכות לעיון במידע ותיקונו">
          <p>
            על פי חוק הגנת הפרטיות, התשמ"א-1981, כל אדם זכאי לעיין במידע המוחזק עליו במאגר מידע. במידה ומצאת שהמידע
            אינו נכון, הינך זכאי לבקש את תיקונו או מחיקתו על ידי פנייה אלינו.
          </p>
        </Section>
        <Section title="8. שינויים במדיניות הפרטיות">
          <p>
            אנו שומרים לעצמנו את הזכות לעדכן את מדיניות הפרטיות מעת לעת. המשך השימוש באתר לאחר שינוי המדיניות מעיד על
            הסכמתך למדיניות המעודכנת.
          </p>
        </Section>
        <Section title="9. יצירת קשר">
          <p>לכל שאלה בנושא פרטיות או לבקשה להסרת מידע, ניתן לפנות אלינו:</p>
          <List
            items={[
              "שם: אור פריאס",
              <span key="t">טלפון: <span dir="ltr">054-572-5681</span></span>,
              "דוא\"ל: OrBagag@gmail.com",
            ]}
          />
        </Section>
      </>
    ),
  },
  accessibility: {
    key: "accessibility",
    title: "הצהרת נגישות",
    updated: "אפריל 2026",
    content: (
      <>
        <p className="mb-4 text-foreground/80 leading-relaxed">
          אנו באור בגג רואים חשיבות עליונה בהנגשת האתר שלנו לכלל האוכלוסייה, כולל אנשים עם מוגבלויות, מתוך אמונה כי
          לכל אדם מגיעה הזכות לחיות בכבוד, בשוויון, בנוחות ובעצמאות. השקענו משאבים ומאמצים על מנת להבטיח שהאתר יהיה
          קל לשימוש ונגיש, מתוך תפיסה כי חוויית גלישה איכותית היא זכות בסיסית.
        </p>
        <Section title="רמת הנגישות באתר">
          <p>
            אתר זה עומד בדרישות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג-2013. התאמות
            הנגישות בוצעו על פי המלצות התקן הישראלי (ת"י 5568) לנגישות תכנים באינטרנט ברמת AA ועל פי מסמך WCAG 2.1
            הבינלאומי.
          </p>
        </Section>
        <Section title="התאמות הנגישות שבוצעו">
          <p>כחלק מתהליך ההנגשה, הוטמעו באתר הפונקציות הבאות:</p>
          <List
            items={[
              <><strong>ניווט מקלדת:</strong> אפשרות מלאה לנווט באתר באמצעות מקשי ה-Tab והחצים ללא שימוש בעכבר.</>,
              <><strong>תמיכה בקוראי מסך:</strong> האתר מותאם לעבודה עם קוראי מסך מובילים (כמו NVDA ו-JAWS) באמצעות שימוש ב-Aria Labels ומבנה סמנטי תקין.</>,
              <><strong>ניגודיות צבעים:</strong> הקפדה על יחס ניגודיות גבוה בין הטקסט לרקע לקריאות מקסימלית.</>,
              <><strong>הגדלת גופנים:</strong> אפשרות לשינוי גודל הטקסט באתר מבלי לפגוע במבנה התוכן.</>,
              <><strong>הדגשת קישורים:</strong> קישורים באתר מסומנים בצורה ברורה (קו תחתי או שינוי צבע בולט).</>,
              <><strong>תגיות Alt:</strong> הוספת תיאור טקסטואלי לתמונות (Alternative Text) עבור לקויי ראייה.</>,
              <><strong>מבנה היררכי:</strong> שימוש נכון בכותרות (H1-H6) לארגון המידע בצורה לוגית.</>,
            ]}
          />
        </Section>
        <Section title="הנחיות להפעלה">
          <List
            items={[
              "האתר נתמך בדפדפנים נפוצים: Chrome, Firefox, Safari, Edge בגרסאותיהם האחרונות.",
              "לניווט באמצעות המקלדת, יש להשתמש במקש Tab למעבר קדימה ו-Shift+Tab למעבר אחורה. מקש Enter משמש להפעלת קישורים.",
            ]}
          />
        </Section>
        <Section title="פרטי רכז נגישות ופניות">
          <p>
            למרות מאמצינו להנגיש את כלל הדפים באתר, ייתכן שיתגלו חלקים מסוימים שטרם הונגשו במלואם. אנו ממשיכים
            במאמצים לשפר את נגישות האתר כחלק ממחויבותנו לאפשר שימוש בו עבור כלל האוכלוסייה.
          </p>
          <p>אם נתקלתם בקושי בגלישה באתר או שיש לכם הצעה לשיפור, נשמח לעמוד לרשותכם:</p>
          <List
            items={[
              "שם רכז הנגישות: אור פריאס",
              <span key="t">טלפון: <span dir="ltr">054-572-5681</span></span>,
              "דוא\"ל: OrBagag@gmail.com",
            ]}
          />
        </Section>
      </>
    ),
  },
};
